import { useState } from 'react';
import styles from './processing-status.module.css';
import ProcessingStatusModal from './processing-status-modal';
import { IconLoader, Spinner, Tag } from '@SBGSports/catapult-core';
import { useTranslations } from 'i18n';
import { ActivityStatus, AthleteStatusResponse } from './activity-status-util';
import { useWindowSize } from 'shared';

export type ActivityProcessingStatus = 'processing' | 'processed' | 'error';
// ToDo - define once endpoint finalised:
// export type AthleteProcessingStatus = 'Pending' | 'Baking' | 'Completed' | 'Error';

export const isActivityProcessing = (status: string): boolean => {
    return (
        status.includes('prepping') ||
        status.includes('baking') ||
        status.includes('loading') ||
        status.includes('queued') ||
        status.includes('debounce_wait')
    );
};

export type ActivityAthletesStatus = { activityId: string; athleteStatuses: AthleteStatusResponse[] };

interface ProcessingStatusButtonProps {
    activityStatuses: ActivityStatus[];
}

const ProcessingStatusButton: React.FC<ProcessingStatusButtonProps> = ({
    activityStatuses,
}: ProcessingStatusButtonProps) => {
    const { width } = useWindowSize();
    const isMobile = width < 768;

    const { __ } = useTranslations();
    const [showProcessingModal, setShowProcessingModal] = useState(false);

    const openModal = () => {
        setShowProcessingModal(true);
        // prevent close button from getting focus on opening the modal
        setTimeout(() => {
            (document.querySelector('[aria-label="Close"]') as HTMLElement)?.blur();
        }, 0);
    };
    const closeModal = () => {
        setShowProcessingModal(false);
        // prevent button from re-gaining focus on closing the modal
        setTimeout(() => {
            document.getElementById('processing-status-button')?.blur();
        }, 0);
    };

    const anyActivityError = Boolean(activityStatuses?.find((a) => a.status.includes('error')));
    const anyActivityProcessing = Boolean(activityStatuses?.find((a) => isActivityProcessing(a.status)));
    return (
        <>
            <div className={`relative ${isMobile ? 'ml-2' : 'mr-2'} `}>
                <Tag
                    className={`px-2 relative ${anyActivityProcessing ? 'py-0.5' : 'py-1'}`}
                    color={anyActivityProcessing ? 'warning' : 'primary'}
                    label={
                        anyActivityProcessing
                            ? __('data_processing.activity_processing')
                            : __('data_processing.processing_history')
                    }
                    id="processing-status-button"
                    handleClick={openModal}
                    leftComponent={{
                        element: (
                            <Spinner
                                size="xs"
                                color="primary"
                                className="mr-1.5"
                                id="processing-status-button-spinner"
                            />
                        ),
                        condition: anyActivityProcessing,
                    }}
                />
                {/* absolutely positioned icon seems to work better not used as Tag rightIcon prop. dot for white bg */}
                {anyActivityError && (
                    <div className={styles['dot']}>
                        <IconLoader
                            iconName="IconErrorWarning"
                            size="sm"
                            className="fill-system-error opacity-100 z-10 absolute"
                            style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '-2px',
                            }}
                        />
                    </div>
                )}
            </div>
            <ProcessingStatusModal
                showProcessingModal={showProcessingModal}
                onClose={closeModal}
                activityStatuses={activityStatuses}
            />
        </>
    );
};

export default ProcessingStatusButton;
